import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Check, X, Star, ArrowRight, Shield, Zap, Users, Globe, 
  Phone, Mail, MessageCircle, Clock, Award, TrendingUp 
} from "lucide-react";
import Layout from "../../components/layout/Layout";
import Footer from "../../components/layout/Footer";
import { useTheme } from "../../components/ThemeContext.tsx";

const PricingPage = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { isDarkMode } = useTheme();

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      subtitle: 'Perfect for small operations',
      icon: Zap,
      monthlyPrice: 299,
      yearlyPrice: 2990,
      savings: 20,
      description: 'Essential monitoring for individual sites',
      features: [
        'Up to 5 MW capacity monitoring',
        'Basic performance analytics',
        'Daily generation reports',
        'Email alerts',
        'Standard support',
        'Mobile app access',
        'Basic weather integration',
        'Monthly performance reports'
      ],
      limitations: [
        'Advanced AI analytics',
        'Predictive maintenance',
        'Custom integrations',
        'Priority support'
      ],
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600',
      recommended: false
    },
    {
      id: 'professional',
      name: 'Professional',
      subtitle: 'Most popular for growing businesses',
      icon: TrendingUp,
      monthlyPrice: 799,
      yearlyPrice: 7990,
      savings: 20,
      description: 'Advanced analytics and optimization',
      features: [
        'Up to 50 MW capacity monitoring',
        'Advanced AI-driven analytics',
        'Real-time performance optimization',
        'Predictive maintenance alerts',
        'Custom dashboards',
        'API access',
        'Weather forecasting integration',
        'Weekly performance reports',
        'Email & SMS alerts',
        'Priority support',
        'Basic drone integration',
        'Energy trading insights'
      ],
      limitations: [
        'Multi-site portfolio management',
        // 'Advanced drone thermography', // Commented out for future use
        'Custom AI model training',
        'Dedicated support team'
      ],
      color: 'emerald',
      gradient: 'from-emerald-500 to-emerald-600',
      recommended: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      subtitle: 'For large-scale operations',
      icon: Globe,
      monthlyPrice: 2499,
      yearlyPrice: 24990,
      savings: 20,
      description: 'Complete portfolio management solution',
      features: [
        'Unlimited capacity monitoring',
        'Full AI analytics suite',
        'Advanced predictive maintenance',
        'Multi-site portfolio management',
        'Custom AI model training',
        // 'Advanced drone thermography', // Commented out for future use
        'White-label solutions',
        'Custom integrations & APIs',
        'Real-time trading optimization',
        'Dedicated support team',
        'On-site training & setup',
        'Custom reporting & analytics',
        '24/7 monitoring & alerts',
        'Regulatory compliance tools',
        'Advanced security features'
      ],
      limitations: [],
      color: 'purple',
      gradient: 'from-purple-500 to-purple-600',
      recommended: false
    }
  ];

  const addOns = [
    {
      name: 'Advanced Drone Services',
      description: 'Comprehensive aerial inspection and thermography',
      monthlyPrice: 499,
      yearlyPrice: 4990,
      features: [
        'Monthly drone inspections',
        'Thermal imaging analysis',
        'Damage detection AI',
        'Maintenance recommendations'
      ]
    },
    {
      name: 'Energy Trading Module',
      description: 'Optimize energy trading and market participation',
      monthlyPrice: 299,
      yearlyPrice: 2990,
      features: [
        'Real-time market analysis',
        'Automated bidding strategies',
        'Revenue optimization',
        'Market trend predictions'
      ]
    },
    {
      name: 'Regulatory Compliance',
      description: 'Automated reporting for regulatory requirements',
      monthlyPrice: 199,
      yearlyPrice: 1990,
      features: [
        'Automated report generation',
        'Compliance monitoring',
        'Audit trail management',
        'Regulatory update tracking'
      ]
    }
  ];

  const faqs = [
    {
      question: "How quickly can I get started?",
      answer: "Setup typically takes 1-2 weeks depending on your infrastructure. Our team will guide you through the entire process including data integration and user training."
    },
    {
      question: "Can I upgrade or downgrade my plan?",
      answer: "Yes, you can change your plan at any time. Upgrades take effect immediately, while downgrades take effect at the next billing cycle."
    },
    {
      question: "What kind of support do you provide?",
      answer: "All plans include support via email and phone. Professional plans get priority support, while Enterprise customers get a dedicated support team and 24/7 availability."
    },
    {
      question: "Do you offer custom integrations?",
      answer: "Yes, we offer custom integrations for Professional and Enterprise plans. Our team can integrate with your existing SCADA systems, weather stations, and other infrastructure."
    },
    {
      question: "Is there a free trial available?",
      answer: "We offer a 30-day free trial for all plans. This includes access to all features so you can evaluate the platform thoroughly before committing."
    },
    {
      question: "How secure is my data?",
      answer: "We use enterprise-grade security with ISO 27001 certification, end-to-end encryption, and SOC 2 compliance. Your data is stored in secure data centers with 99.9% uptime guarantee."
    }
  ];

  const getPrice = (plan) => {
    const price = billingPeriod === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
    return billingPeriod === 'monthly' ? price : Math.round(price / 12);
  };

  const getSavings = (plan) => {
    if (billingPeriod === 'yearly') {
      const monthlyCost = plan.monthlyPrice * 12;
      const savings = monthlyCost - plan.yearlyPrice;
      return Math.round((savings / monthlyCost) * 100);
    }
    return 0;
  };

  return (
    <Layout header={6}>
      <div className={`min-h-screen ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
        {/* Hero Section */}
        <section className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Transparent Pricing
              </span>
              <h1 className={`text-4xl lg:text-6xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Choose Your Perfect Plan
              </h1>
              <p className={`text-xl max-w-3xl mx-auto mb-8 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Scale your renewable energy operations with our flexible pricing plans. 
                Start with what you need and grow as your portfolio expands.
              </p>
              
              {/* Billing Toggle */}
              <div className="flex items-center justify-center gap-4 mb-12">
                <span className={`font-medium ${
                  billingPeriod === 'monthly' 
                    ? isDarkMode ? 'text-white' : 'text-gray-900'
                    : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Monthly
                </span>
                <button
                  onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
                  className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                    billingPeriod === 'yearly' ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <div className={`absolute w-5 h-5 rounded-full bg-white transition-transform duration-300 top-1 ${
                    billingPeriod === 'yearly' ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
                <span className={`font-medium ${
                  billingPeriod === 'yearly' 
                    ? isDarkMode ? 'text-white' : 'text-gray-900'
                    : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Yearly
                </span>
                {billingPeriod === 'yearly' && (
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
                    Save 20%
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pricing Plans */}
        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className={`relative rounded-2xl border-2 p-8 ${
                    plan.recommended 
                      ? 'border-blue-500 shadow-xl scale-105' 
                      : isDarkMode 
                        ? 'border-slate-700 shadow-lg' 
                        : 'border-gray-200 shadow-lg'
                  } ${
                    isDarkMode ? 'bg-slate-800' : 'bg-white'
                  } hover:shadow-2xl transition-all duration-300`}
                >
                  {plan.recommended && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-blue-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-r ${plan.gradient} flex items-center justify-center`}>
                      <plan.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className={`text-2xl font-bold mb-2 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {plan.name}
                    </h3>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {plan.subtitle}
                    </p>
                  </div>

                  <div className="text-center mb-8">
                    <div className="flex items-baseline justify-center">
                      <span className={`text-4xl font-bold ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        ${getPrice(plan).toLocaleString()}
                      </span>
                      <span className={`text-lg ml-1 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        /month
                      </span>
                    </div>
                    {billingPeriod === 'yearly' && getSavings(plan) > 0 && (
                      <p className="text-green-600 text-sm mt-1">
                        Save {getSavings(plan)}% with yearly billing
                      </p>
                    )}
                    <p className={`text-sm mt-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {plan.description}
                    </p>
                  </div>

                  <div className="mb-8">
                    <h4 className={`font-semibold mb-4 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      Everything included:
                    </h4>
                    <ul className="space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className={`text-sm ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                    
                    {plan.limitations.length > 0 && (
                      <div className="mt-6">
                        <h5 className={`text-sm font-medium mb-3 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Not included:
                        </h5>
                        <ul className="space-y-2">
                          {plan.limitations.map((limitation, limitIndex) => (
                            <li key={limitIndex} className="flex items-start gap-3">
                              <X className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                              <span className={`text-sm ${
                                isDarkMode ? 'text-gray-500' : 'text-gray-400'
                              }`}>
                                {limitation}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                      plan.recommended
                        ? 'bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600 text-white shadow-lg hover:shadow-xl'
                        : isDarkMode
                          ? 'bg-slate-700 hover:bg-slate-600 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Add-ons Section */}
        <section className={`py-20 ${
          isDarkMode ? 'bg-slate-800' : 'bg-white'
        }`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className={`text-3xl lg:text-4xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Enhance Your Plan
              </h2>
              <p className={`text-xl max-w-3xl mx-auto ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Extend your capabilities with our specialized add-on modules
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {addOns.map((addon, index) => (
                <motion.div
                  key={addon.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className={`rounded-xl p-6 border ${
                    isDarkMode 
                      ? 'bg-slate-700 border-slate-600' 
                      : 'bg-gray-50 border-gray-200'
                  } hover:shadow-lg transition-all duration-300`}
                >
                  <h3 className={`text-xl font-bold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {addon.name}
                  </h3>
                  <p className={`mb-4 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {addon.description}
                  </p>
                  
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className={`text-2xl font-bold ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        ${billingPeriod === 'monthly' ? addon.monthlyPrice : Math.round(addon.yearlyPrice / 12)}
                      </span>
                      <span className={`text-sm ml-1 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        /month
                      </span>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {addon.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button className={`w-full py-2 px-4 rounded-lg font-medium transition-all duration-300 ${
                    isDarkMode
                      ? 'border border-slate-500 text-slate-300 hover:bg-slate-600'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}>
                    Add to Plan
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className={`text-3xl lg:text-4xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Frequently Asked Questions
              </h2>
              <p className={`text-xl ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Everything you need to know about our pricing and platform
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`rounded-xl p-6 ${
                    isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'
                  }`}
                >
                  <h3 className={`text-lg font-semibold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {faq.question}
                  </h3>
                  <p className={`${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-emerald-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Ready to Optimize Your Renewable Energy Operations?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Start your 30-day free trial today. No credit card required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2">
                  Start Free Trial
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2">
                  <Phone className="w-5 h-5" />
                  Schedule Demo
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
      <Footer footer={6} />
    </Layout>
  );
};

export default PricingPage;
